import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useImperativeHandle } from 'react';
import { Trash2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { cn } from 'src/lib/utils';

import { type IAdditionalFieldType, cryptographicKeyFormSchema, type ICryptographicKeyForm } from '../../model';

import MoreOptionsDropdown from './MoreOptionsDropdown';
import DynamicFieldInput from './DynamicFieldInput';

type Props = {
  ref: React.RefObject<{ submit: (callback: (data: ICryptographicKeyForm) => void) => void } | null>;
};

const CryptographicKeyForm = ({ ref }: Props) => {
  const form = useForm<ICryptographicKeyForm>({
    resolver: zodResolver(cryptographicKeyFormSchema),
    defaultValues: {
      name: '',
      providerName: '',
      key: '',
      description: '',
      additionalFields: [],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'additionalFields',
  });

  useImperativeHandle(
    ref,
    () => ({
      submit: (callback) => form.handleSubmit(callback)(),
    }),
    [],
  );

  const onSelectMoreOption = (type: IAdditionalFieldType) => append({ type, value: '' });

  return (
    <div className="space-y-4">
      <Form {...form}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Credential name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormDescription>
                Add an asset name to easily identify these credentials in the deposit table.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="providerName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Provider</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormDescription>Add the name of the provider these credentials access.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cryptographic key</FormLabel>
              <FormControl>
                <Textarea {...field} className="resize-none h-[80px]" />
              </FormControl>
              <FormDescription>
                Paste your cryptographic key here — this could be an SSH key or similar.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} className="resize-none h-[80px]" />
              </FormControl>
              <FormDescription>
                Describe what this account is used for, and the role it plays in recovering application functionality.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Separator />
        {fields.map((field, index) => (
          <FormField
            key={field.id}
            control={form.control}
            name={`additionalFields.${index}.value`}
            render={({ field: inputField }) => (
              <FormItem>
                <FormLabel className={cn('capitalize', field.type === 'url' && 'uppercase')}>{field.type}</FormLabel>
                <div className="flex items-center gap-2">
                  <DynamicFieldInput type={field.type} field={inputField} />
                  <Button variant="destructive" size="icon" className="shrink-0" onClick={() => remove(index)}>
                    <Trash2 />
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <MoreOptionsDropdown onSelectOption={onSelectMoreOption} />
      </Form>
    </div>
  );
};

export default CryptographicKeyForm;
